import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Check,
  Clock,
  Crown,
  CreditCard,
  Flame,
  PlusCircle,
  RefreshCw,
  RotateCcw,
  Save,
  Shield,
  Trash2,
  X,
  Zap,
  Pencil,
  Filter,
} from "lucide-react";
import toast from "react-hot-toast";

import { GetCreditPackages } from "./apis/get";
import {
  PostCreateCreditPackage,
} from "./apis/post";
import {
  PatchUpdateCreditPackage,
} from "./apis/patch";
import {
  DeleteCreditPackage,
} from "./apis/delete";
import {
  PutReactivateCreditPackage,
} from "./apis/put";
import LoaderService from "../services/LoaderService";

interface CreditPackage {
  id: number;
  name: string;
  description: string | null;
  invoice_description?: string | null;
  credits_amount: number;
  price_in_paise?: number | null;
  price_in_rupees?: number | null;
  discounted_price_in_rupees?: number | null;
  discount_percentage?: number | null;
  is_popular?: boolean;
  sort_order?: number | null;
  validity_days?: number | null;
  credits_per_rupee?: number | null;
  platform: string;
  is_active?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

interface PackageFormState {
  name: string;
  description: string;
  invoice_description: string;
  credits_amount: string;
  price_in_rupees: string;
  platform: string;
  discount_percentage: string;
  is_popular: boolean;
  sort_order: string;
  validity_days: string;
}

const emptyForm = (): PackageFormState => ({
  name: "",
  description: "",
  invoice_description: "",
  credits_amount: "",
  price_in_rupees: "",
  platform: "android",
  discount_percentage: "",
  is_popular: false,
  sort_order: "",
  validity_days: "",
});

const INPUT_CLASS =
  "w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-slate-200 focus:outline-none focus:border-blue-400 disabled:opacity-50 disabled:cursor-not-allowed";

const normalizeRole = (role: string | null) => {
  const lowered = (role || "").toLowerCase();
  if (["customer_support", "customer_lead", "calling_team_lead"].includes(lowered)) {
    return "support";
  }
  return lowered;
};

const toNumber = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatRupees = (amount: number | null | undefined) => {
  const value = Number(amount || 0);
  return `₹${value.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
};

const normalizePackage = (pkg: Partial<CreditPackage>): CreditPackage => {
  const priceInRupees =
    pkg.price_in_rupees ??
    (pkg.price_in_paise !== undefined && pkg.price_in_paise !== null
      ? Number(pkg.price_in_paise) / 100
      : 0);
  const discountPercentage = Number(pkg.discount_percentage || 0);
  const discountedPrice =
    pkg.discounted_price_in_rupees ??
    (priceInRupees && discountPercentage
      ? priceInRupees - (priceInRupees * discountPercentage) / 100
      : priceInRupees);

  return {
    id: Number(pkg.id || 0),
    name: pkg.name || "",
    description: pkg.description ?? null,
    invoice_description: pkg.invoice_description ?? null,
    credits_amount: Number(pkg.credits_amount || 0),
    price_in_paise:
      pkg.price_in_paise ??
      (pkg.price_in_rupees !== undefined && pkg.price_in_rupees !== null
        ? Math.round(Number(pkg.price_in_rupees) * 100)
        : null),
    price_in_rupees: priceInRupees,
    discounted_price_in_rupees: discountedPrice,
    discount_percentage: discountPercentage,
    is_popular: Boolean(pkg.is_popular),
    sort_order: pkg.sort_order ?? null,
    validity_days: pkg.validity_days ?? null,
    credits_per_rupee: pkg.credits_per_rupee ?? null,
    platform: pkg.platform || "android",
    is_active: pkg.is_active ?? true,
    created_at: pkg.created_at ?? null,
    updated_at: pkg.updated_at ?? null,
  };
};

const buildPayload = (form: PackageFormState) => {
  const payload: Record<string, any> = {
    name: form.name.trim(),
    credits_amount: Number(form.credits_amount || 0),
    price_in_paise: Math.round(Number(form.price_in_rupees || 0) * 100),
    platform: form.platform.trim(),
    is_popular: form.is_popular,
  };

  if (form.description.trim()) payload.description = form.description.trim();
  if (form.invoice_description.trim()) {
    payload.invoice_description = form.invoice_description.trim();
  }

  const discountPercentage = toNumber(form.discount_percentage);
  if (discountPercentage !== null) payload.discount_percentage = discountPercentage;

  const sortOrder = toNumber(form.sort_order);
  if (sortOrder !== null) payload.sort_order = sortOrder;

  const validityDays = toNumber(form.validity_days);
  if (validityDays !== null) payload.validity_days = validityDays;

  return payload;
};

const isValidPackageResponse = (response: any) =>
  response?.status === true || response?.status === "success";

const getResponseErrorMessage = (response: any, fallback: string) => {
  if (!response) return fallback;
  if (typeof response === "string") return response;
  if (Array.isArray(response.detail)) {
    return response.detail
      .map((item: any) =>
        typeof item === "object" && item !== null
          ? `${item.field ? `${item.field}: ` : ""}${item.message || JSON.stringify(item)}`
          : String(item),
      )
      .join(", ");
  }
  return response.message || response.detail || fallback;
};

const Subscriptions = () => {
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "active" | "inactive">("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [editingPackageId, setEditingPackageId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<PackageFormState>(emptyForm());

  useEffect(() => {
    const data = localStorage.getItem("data");
    if (!data) return;

    try {
      const parsed = JSON.parse(data);
      setRole(parsed.Role || null);
    } catch (error) {
      console.error("Failed to read role for subscriptions", error);
    }
  }, []);

  const normalizedRole = normalizeRole(role);
  const canManageSubscriptions = normalizedRole === "super_admin";
  const effectiveFilter = canManageSubscriptions ? filter : "active";

  const loadPackages = useCallback(
    async () => {
      setLoading(true);
      try {
        LoaderService.show();
        const response = await GetCreditPackages(
          effectiveFilter === "all" ? undefined : effectiveFilter === "active",
        );

        if (isValidPackageResponse(response)) {
          const rawPackages =
            response?.data?.packages ??
            response?.data?.items ??
            response?.data ??
            [];
          const normalizedPackages = Array.isArray(rawPackages)
            ? rawPackages.map((item: CreditPackage) => normalizePackage(item))
            : [];
          setPackages(normalizedPackages);
        } else {
          setPackages([]);
          toast.error(getResponseErrorMessage(response, "Failed to fetch packages"));
        }
      } catch (error) {
        console.error("Error fetching packages:", error);
        toast.error("Something went wrong while fetching packages");
      } finally {
        setLoading(false);
        LoaderService.hide();
      }
    },
    [effectiveFilter],
  );

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const filteredPackages = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return packages;

    return packages.filter((pkg) => {
      const haystack = [
        pkg.name,
        pkg.description,
        pkg.invoice_description,
        pkg.platform,
        String(pkg.credits_amount ?? ""),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [packages, search]);

  const stats = useMemo(() => {
    const totalPackages = packages.length;
    const activePackages = packages.filter((pkg) => pkg.is_active !== false).length;
    const inactivePackages = packages.filter((pkg) => pkg.is_active === false).length;
    const popularPackages = packages.filter((pkg) => pkg.is_popular).length;

    return {
      totalPackages,
      activePackages,
      inactivePackages,
      popularPackages,
    };
  }, [packages]);

  const openCreateForm = () => {
    setFormMode("create");
    setEditingPackageId(null);
    setForm(emptyForm());
    setFormOpen(true);
  };

  const openEditForm = (pkg: CreditPackage) => {
    setFormMode("edit");
    setEditingPackageId(pkg.id);
    setForm({
      name: pkg.name || "",
      description: pkg.description || "",
      invoice_description: pkg.invoice_description || "",
      credits_amount: String(pkg.credits_amount ?? ""),
      price_in_rupees: String(pkg.price_in_rupees ?? ""),
      platform: pkg.platform || "android",
      discount_percentage:
        pkg.discount_percentage === null || pkg.discount_percentage === undefined
          ? ""
          : String(pkg.discount_percentage),
      is_popular: Boolean(pkg.is_popular),
      sort_order:
        pkg.sort_order === null || pkg.sort_order === undefined
          ? ""
          : String(pkg.sort_order),
      validity_days:
        pkg.validity_days === null || pkg.validity_days === undefined
          ? ""
          : String(pkg.validity_days),
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setForm(emptyForm());
    setEditingPackageId(null);
    setFormMode("create");
  };

  const handleSearch = async () => {
    setSearch(searchInput.trim());
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canManageSubscriptions) return;

    const requiredMissing = [
      !form.name.trim() ? "name" : "",
      !form.credits_amount.trim() ? "credits_amount" : "",
      !form.price_in_rupees.trim() ? "price_in_rupees" : "",
      !form.platform.trim() ? "platform" : "",
    ].filter(Boolean);

    if (requiredMissing.length > 0) {
      toast.error(`Missing required fields: ${requiredMissing.join(", ")}`);
      return;
    }

    const payload = buildPayload(form);
    setSaving(true);
    try {
      const response =
        formMode === "create"
          ? await PostCreateCreditPackage(payload)
          : await PatchUpdateCreditPackage(editingPackageId as number, payload);

      if (isValidPackageResponse(response)) {
        closeForm();
        await loadPackages();
      } else {
        toast.error(getResponseErrorMessage(response, "Unable to save package"));
      }
    } catch (error) {
      console.error("Failed to save package", error);
      toast.error("Failed to save package");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (pkg: CreditPackage) => {
    if (!canManageSubscriptions) return;
    const confirmed = window.confirm(
      `Deactivate ${pkg.name}? This will hide the package from active customers.`,
    );
    if (!confirmed) return;

    const response = await DeleteCreditPackage(pkg.id);
    if (isValidPackageResponse(response)) {
      await loadPackages();
    } else {
      toast.error(getResponseErrorMessage(response, "Failed to deactivate package"));
    }
  };

  const handleReactivate = async (pkg: CreditPackage) => {
    if (!canManageSubscriptions) return;

    const response = await PutReactivateCreditPackage(pkg.id);
    if (isValidPackageResponse(response)) {
      await loadPackages();
    } else {
      toast.error(getResponseErrorMessage(response, "Failed to reactivate package"));
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-200 p-6 lg:p-12 animate-in fade-in duration-700">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="text-center space-y-4">
          <h1 className="text-4xl lg:text-6xl font-black tracking-tight text-white uppercase italic">
            Credit <span className="text-blue-500">Packages</span>
          </h1>
          <p className="text-slate-400 max-w-2xl mx-auto font-medium text-lg">
            Empower your matchmaking experience with premium credit packs.
            Super admins can create, update, deactivate, and reactivate plans from here.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-4">
            <span className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500 bg-slate-800/50 px-3 py-1 rounded-full border border-slate-700">
              <Shield className="w-3 h-3 text-blue-500" /> Secure Payments
            </span>
            <span className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500 bg-slate-800/50 px-3 py-1 rounded-full border border-slate-700">
              <Zap className="w-3 h-3 text-yellow-500" /> Instant Credits
            </span>
            {canManageSubscriptions && (
              <span className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-amber-400 bg-amber-500/10 px-3 py-1 rounded-full border border-amber-500/20">
                <Shield className="w-3 h-3" /> Super Admin Mode
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          <StatCard
            label="Total Packages"
            value={String(stats.totalPackages)}
            hint="All loaded credit packages"
            icon={<CreditCard className="w-5 h-5" />}
            tone="blue"
          />
          <StatCard
            label="Active Packages"
            value={String(stats.activePackages)}
            hint="Live in customer-facing views"
            icon={<Check className="w-5 h-5" />}
            tone="emerald"
          />
          <StatCard
            label="Inactive Packages"
            value={String(stats.inactivePackages)}
            hint="Hidden until reactivated"
            icon={<Trash2 className="w-5 h-5" />}
            tone="rose"
          />
          <StatCard
            label="Popular Packages"
            value={String(stats.popularPackages)}
            hint="Marked as featured"
            icon={<Flame className="w-5 h-5" />}
            tone="amber"
          />
        </div>

        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 bg-slate-900/40 border border-slate-800 rounded-3xl p-5">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-slate-950 border border-slate-800 text-blue-400">
              <Filter className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-sm font-black text-white uppercase tracking-widest">
                Package Filters
              </h2>
              <p className="text-slate-500 text-xs">
                Search the package catalog and, for super admins, switch between active and inactive packages.
              </p>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-3 w-full xl:w-auto">
            {canManageSubscriptions && (
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as "all" | "active" | "inactive")}
                className="px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-slate-200 focus:outline-none focus:border-blue-400"
              >
                <option value="all">All packages</option>
                <option value="active">Active only</option>
                <option value="inactive">Inactive only</option>
              </select>
            )}

            {!canManageSubscriptions && (
              <div className="px-4 py-3 rounded-2xl bg-slate-950 border border-slate-800 text-slate-400 text-sm font-semibold">
                View-only access
              </div>
            )}

            <div className="flex flex-1 min-w-0 gap-3">
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                placeholder="Search name, description, platform..."
                className="flex-1 min-w-0 px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-slate-200 focus:outline-none focus:border-blue-400"
              />
              <button
                type="button"
                onClick={handleSearch}
                className="px-5 py-3 bg-slate-800 hover:bg-slate-700 rounded-2xl font-bold text-white transition active:scale-95"
              >
                Apply
              </button>
            </div>

            <button
              onClick={() => loadPackages()}
              className="flex items-center justify-center gap-2 px-5 py-3 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-2xl text-slate-200 font-bold transition active:scale-95"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>

            {canManageSubscriptions && (
              <button
                onClick={openCreateForm}
                className="flex items-center justify-center gap-2 px-5 py-3 bg-blue-500 hover:bg-blue-400 text-white border border-blue-300 rounded-2xl font-black transition active:scale-95"
              >
                <PlusCircle size={16} />
                Create Package
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading ? (
            <div className="col-span-full py-20 text-center text-slate-500 font-bold bg-slate-900/40 border border-slate-800 rounded-3xl">
              Loading packages...
            </div>
          ) : filteredPackages.length === 0 ? (
            <div className="col-span-full py-20 text-center text-slate-500 font-bold bg-slate-900/40 border border-slate-800 rounded-3xl">
              No packages found for the current filters.
            </div>
          ) : (
            filteredPackages.map((pkg) => {
              const isPopular = Boolean(pkg.is_popular);
              const isActive = pkg.is_active !== false;
              const platformTone =
                pkg.platform.toLowerCase() === "android"
                  ? "bg-emerald-600"
                  : "bg-indigo-600";

              return (
                <div
                  key={pkg.id}
                  className={`relative flex flex-col group transition-all duration-500 hover:-translate-y-2 ${
                    isPopular
                      ? "bg-gradient-to-b from-blue-600/20 to-slate-900 border-2 border-blue-500/50 rounded-3xl p-8 shadow-[0_0_50px_rgba(59,130,246,0.2)]"
                      : isActive
                        ? "bg-slate-900/50 backdrop-blur-xl border border-slate-800 rounded-3xl p-8 hover:border-slate-700"
                        : "bg-slate-900/30 backdrop-blur-xl border border-slate-800/80 rounded-3xl p-8 opacity-80"
                  }`}
                >
                  <div className="absolute -top-4 inset-x-0 flex justify-center gap-2 flex-wrap">
                    {isPopular && (
                      <div className="bg-blue-500 text-white text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full shadow-lg flex items-center gap-2">
                        <Flame className="w-3 h-3 animate-pulse" /> Most Popular
                      </div>
                    )}
                    <div
                      className={`text-white text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full shadow-lg flex items-center gap-2 ${platformTone}`}
                    >
                      {pkg.platform.toUpperCase()}
                    </div>
                    {!isActive && (
                      <div className="bg-rose-500 text-white text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full shadow-lg">
                        Inactive
                      </div>
                    )}
                  </div>

                  <div className="mb-8 flex justify-between items-start mt-4 gap-4">
                    <div>
                      <h3 className="text-2xl font-black text-white mb-1 uppercase tracking-tight italic">
                        {(pkg.name || "Untitled").replace(/_/g, " ")}
                      </h3>
                      <p className="text-slate-400 text-sm font-medium">
                        {pkg.description || "No description provided."}
                      </p>
                    </div>
                    <div
                      className={`p-3 rounded-2xl ${
                        isPopular
                          ? "bg-blue-500 text-white shadow-lg shadow-blue-500/50"
                          : isActive
                            ? "bg-slate-800 text-slate-400"
                            : "bg-rose-500/10 text-rose-400 border border-rose-500/20"
                      }`}
                    >
                      {pkg.credits_amount > 20 ? (
                        <Crown className="w-6 h-6" />
                      ) : isPopular ? (
                        <Zap className="w-6 h-6" />
                      ) : (
                        <CreditCard className="w-6 h-6" />
                      )}
                    </div>
                  </div>

                  <div className="mb-8">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl lg:text-5xl font-black text-white tracking-tighter italic">
                        {formatRupees(pkg.discounted_price_in_rupees)}
                      </span>
                      {Number(pkg.discount_percentage || 0) > 0 && (
                        <span className="text-slate-500 line-through text-lg font-bold">
                          {formatRupees(pkg.price_in_rupees)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {Number(pkg.discount_percentage || 0) > 0 && (
                        <span className="bg-emerald-500/10 text-emerald-500 text-[10px] font-black px-2 py-0.5 rounded border border-emerald-500/20 uppercase">
                          Save {pkg.discount_percentage}%
                        </span>
                      )}
                      <span className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">
                        Per Purchase
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4 flex-grow">
                    <FeatureRow label={pkg.description || "Credit package"} icon={<Check className="w-3 h-3 text-blue-400" />} />
                    <FeatureRow
                      label={`${pkg.credits_amount} Premium Credits`}
                      icon={<Zap className="w-3 h-3 text-blue-400" />}
                    />
                    {pkg.validity_days ? (
                      <FeatureRow
                        label={`Valid for ${pkg.validity_days} Days`}
                        icon={<Clock className="w-3 h-3 text-blue-400" />}
                      />
                    ) : null}
                    {pkg.invoice_description ? (
                      <FeatureRow
                        label={pkg.invoice_description}
                        icon={<CreditCard className="w-3 h-3 text-emerald-400" />}
                      />
                    ) : (
                      <FeatureRow
                        label="Instant Account Activation"
                        icon={<Zap className="w-3 h-3 text-emerald-400" />}
                      />
                    )}
                  </div>

                  {canManageSubscriptions && (
                    <div className="mt-8 pt-6 border-t border-slate-800/70 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => openEditForm(pkg)}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-slate-800 hover:bg-slate-700 text-white font-bold transition active:scale-95"
                      >
                        <Pencil size={14} />
                        Edit
                      </button>
                      {isActive ? (
                        <button
                          type="button"
                          onClick={() => handleDeactivate(pkg)}
                          className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/20 font-bold transition active:scale-95"
                        >
                          <Trash2 size={14} />
                          Deactivate
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleReactivate(pkg)}
                          className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/20 font-bold transition active:scale-95"
                        >
                          <RotateCcw size={14} />
                          Reactivate
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {formOpen && canManageSubscriptions && (
        <div className="fixed inset-0 z-[600] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-[2rem] border border-slate-800 bg-[#0b1220] shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-800 px-6 py-5">
              <div>
                <h2 className="text-2xl font-black text-white uppercase italic tracking-tight">
                  {formMode === "create" ? "Create Credit Package" : "Update Credit Package"}
                </h2>
                <p className="text-slate-500 text-sm mt-1">
                  {formMode === "create"
                    ? "Create a new package for the subscriptions page."
                    : "Update the package details shown to admins and customers."}
                </p>
              </div>
              <button
                type="button"
                onClick={closeForm}
                className="p-2 rounded-xl hover:bg-slate-800 text-slate-400 hover:text-white transition"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldWrap label="Name">
                  <input
                    value={form.name}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className={INPUT_CLASS}
                    placeholder="Starter Pack"
                  />
                </FieldWrap>

                <FieldWrap label="Platform">
                  <input
                    value={form.platform}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, platform: e.target.value }))
                    }
                    className={INPUT_CLASS}
                    placeholder="android"
                  />
                </FieldWrap>

                <FieldWrap label="Credits Amount">
                  <input
                    type="number"
                    min={1}
                    value={form.credits_amount}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        credits_amount: e.target.value,
                      }))
                    }
                    className={INPUT_CLASS}
                  />
                </FieldWrap>

                <FieldWrap label="Price in Rupees">
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.price_in_rupees}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        price_in_rupees: e.target.value,
                      }))
                    }
                    className={INPUT_CLASS}
                    placeholder="499"
                  />
                </FieldWrap>

                <FieldWrap label="Discount Percentage">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={form.discount_percentage}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        discount_percentage: e.target.value,
                      }))
                    }
                    className={INPUT_CLASS}
                    placeholder="0"
                  />
                </FieldWrap>

                <FieldWrap label="Sort Order">
                  <input
                    type="number"
                    value={form.sort_order}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, sort_order: e.target.value }))
                    }
                    className={INPUT_CLASS}
                    placeholder="Optional"
                  />
                </FieldWrap>

                <FieldWrap label="Validity Days">
                  <input
                    type="number"
                    min={1}
                    value={form.validity_days}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        validity_days: e.target.value,
                      }))
                    }
                    className={INPUT_CLASS}
                    placeholder="Optional"
                  />
                </FieldWrap>

                <div className="md:col-span-2">
                  <FieldWrap label="Description">
                    <textarea
                      value={form.description}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      className={`${INPUT_CLASS} min-h-[110px] resize-y`}
                      placeholder="Describe the package benefits."
                    />
                  </FieldWrap>
                </div>

                <div className="md:col-span-2">
                  <FieldWrap label="Invoice Description">
                    <textarea
                      value={form.invoice_description}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          invoice_description: e.target.value,
                        }))
                      }
                      className={`${INPUT_CLASS} min-h-[110px] resize-y`}
                      placeholder="Shown on the invoice/receipt."
                    />
                  </FieldWrap>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-2xl border border-slate-800 bg-slate-950 px-4 py-3">
                <div>
                  <div className="text-white font-bold text-sm">Mark as popular</div>
                  <div className="text-slate-500 text-xs">
                    Popular packages are highlighted in the customer-facing views.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setForm((prev) => ({ ...prev, is_popular: !prev.is_popular }))
                  }
                  className={`relative inline-flex h-7 w-14 items-center rounded-full transition ${
                    form.is_popular ? "bg-blue-500" : "bg-slate-700"
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                      form.is_popular ? "translate-x-8" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-6 py-3 rounded-2xl bg-slate-800 hover:bg-slate-700 font-bold text-white transition active:scale-95"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-blue-500 hover:bg-blue-400 disabled:opacity-50 disabled:cursor-not-allowed font-black text-white transition active:scale-95"
                >
                  <Save size={16} />
                  {saving
                    ? "Saving..."
                    : formMode === "create"
                      ? "Create Package"
                      : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

function FeatureRow({
  label,
  icon,
}: {
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center border border-blue-500/30">
        {icon}
      </div>
      <p className="text-sm font-bold text-slate-300">{label}</p>
    </div>
  );
}

function FieldWrap({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="space-y-2 block">
      <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-black">
        {label}
      </span>
      {children}
    </label>
  );
}

function StatCard({
  label,
  value,
  hint,
  icon,
  tone,
}: {
  label: string;
  value: string;
  hint: string;
  icon: React.ReactNode;
  tone: "amber" | "emerald" | "blue" | "rose";
}) {
  const toneMap = {
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/20",
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    blue: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    rose: "text-rose-400 bg-rose-500/10 border-rose-500/20",
  };

  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-[2rem] p-5 shadow-xl">
      <div className="flex items-center justify-between mb-5">
        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
          {label}
        </span>
        <div className={`p-3 rounded-2xl border ${toneMap[tone]}`}>{icon}</div>
      </div>
      <div className="text-3xl font-black text-white tracking-tight">{value}</div>
      <div className="text-xs text-slate-500 mt-2">{hint}</div>
    </div>
  );
}

export default Subscriptions;
